import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import load from 'loadjs';
import cc from 'card-validator';

import { LayoutComponent_LocalLoader as Loader } from 'ggApp/modules/layout';
import {
    CREDIT_CARD_MASK_SYMBOL,
    DEFAULT_CARD_MASKS,
    AMEX_CARD_MASKS,
    PAYMENT_CARD_INPUT_IDS,
} from 'ggApp/modules/payment/constants';
import { ADYEN_PAYMENT_CC } from 'ggApp/modules/adyen/constants';
import { PureTranslator } from 'ggApp/modules/translator';
import { validateCCNumber, validateCCExp, validateCCCvv } from 'ggApp/modules/form/validations';

import loadConfiguration from 'ggApp/utils/configuration';

import useMount from 'ggApp/shared/hooks/useMount';

import { Form, FormRow, Input, ErrorText, SubmitButton } from './styles';

const { ADYEN_PUBLIC_KEY, ADYEN_LIBRARY_URL } = loadConfiguration();

export const getCardInputMasks = (rawCardNumber) => {
    const cardNumber = rawCardNumber
        .split(CREDIT_CARD_MASK_SYMBOL)
        .filter(Boolean)
        .join('');
    const { card } = cc.number(cardNumber);
    if (!card) return null;
    return card.isAmex ? AMEX_CARD_MASKS : DEFAULT_CARD_MASKS;
};

export const normalizeCardData = ({ cardNumber, cvc, holderName, expiry }) => {
    const expArray = expiry ? expiry.split('/') : [];
    return {
        cardNumber: cardNumber && cardNumber.replace(/\s/g, ''),
        cvc: cvc && cvc.replace(/\s/g, ''),
        holderName: holderName.toUpperCase(),
        expiryMonth: expArray[0] ? expArray[0].replace(/\s/g, '') : '',
        expiryYear: expArray[1] ? expArray[1].replace(/\s/g, '') : '',
    };
};

const AdyenCardForm = ({
    className,
    submitPayment,
    resetSubmittedPaymentError,
    orderNumber,
    onLegacyPaymentMethodCreated,
    adyenSubmittedPayment,
}) => {
    const {
        errors,
        control,
        formState: { isValid },
        handleSubmit,
        watch,
    } = useForm({
        mode: 'onBlur',
    });

    const watchCardNumberField = watch('cardNumber');

    const [canUpdateState, setCanUpdateState] = useState(true);
    const [loadingAdyenLibrary, setLoadingAdyenLibrary] = useState(true);
    const [adyenInstance, setAdyenInstance] = useState(true);
    const [cardMasks, setCardMasks] = useState(DEFAULT_CARD_MASKS);

    const adyenOnLoadError = () => {
        if (canUpdateState) {
            setLoadingAdyenLibrary(false);
        }
    };

    const adyenOnLoad = () => {
        if (canUpdateState) {
            setLoadingAdyenLibrary(false);
            if (window && window.adyen) {
                setAdyenInstance(window.adyen.createEncryption(ADYEN_PUBLIC_KEY));
            }
        }
    };

    const encryptMyData = ({ cardNumber, cvc, holderName, expiryMonth, expiryYear }) => {
        const data = {
            number: cardNumber,
            cvc,
            holderName,
            expiryMonth,
            expiryYear: `20${expiryYear}`,
            generationtime: `${new Date().toISOString()}`,
        };

        return adyenInstance.encrypt(data);
    };

    const validateField = (fieldName, fieldIsValid) => fieldIsValid;

    const submitForm = (values) => {
        const normalizedData = normalizeCardData(values);
        const encryptedData = encryptMyData(normalizedData);
        submitPayment(
            ADYEN_PAYMENT_CC,
            {
                card: normalizedData,
                encryptedCard: encryptedData,
            },
            orderNumber,
        ).then(onLegacyPaymentMethodCreated);
    };

    useMount(() => {
        if (ADYEN_LIBRARY_URL) {
            load(ADYEN_LIBRARY_URL, {
                success: adyenOnLoad,
                error: adyenOnLoadError,
            });
        }
        setCanUpdateState(false);
        resetSubmittedPaymentError();
    });

    useEffect(() => {
        const cardNumber = control.getValues('cardNumber');
        const cardInputMasks = cardNumber && getCardInputMasks(cardNumber);
        if (cardInputMasks) {
            setCardMasks(cardInputMasks);
        }
    }, [control, watchCardNumberField]);

    if (loadingAdyenLibrary) {
        return <Loader dark main active={loadingAdyenLibrary} />;
    }

    return (
        <Form className={className} onSubmit={handleSubmit(submitForm)}>
            <FormRow>
                <Controller
                    name="cardNumber"
                    control={control}
                    as={
                        <Input
                            id={PAYMENT_CARD_INPUT_IDS.CARD_NUMBER}
                            label={<PureTranslator tk="NEW_CHECKOUT_PAYMENT_FORM_CARD_NUMBER" />}
                            maskProps={{
                                mask: cardMasks.cardNumberMask,
                                maskChar: null,
                            }}
                            error={
                                errors.cardNumber && (
                                    <PureTranslator tk="NEW_CHECKOUT_PAYMENT_FORM_CARD_NUMBER_ERROR" />
                                )
                            }
                        />
                    }
                    rules={{
                        required: true,
                        validate: () =>
                            validateField(
                                'cardNumber',
                                validateCCNumber({
                                    asBool: true,
                                    number: control.getValues('cardNumber'),
                                }),
                            ),
                    }}
                />
            </FormRow>
            <FormRow>
                <Controller
                    name="holderName"
                    control={control}
                    as={
                        <Input
                            id={PAYMENT_CARD_INPUT_IDS.CARD_HOLDER}
                            label={
                                <PureTranslator tk="NEW_CHECKOUT_PAYMENT_FORM_CARDHOLDER_NAME" />
                            }
                            error={
                                errors.holderName && (
                                    <PureTranslator tk="NEW_CHECKOUT_PAYMENT_FORM_CARDHOLDER_NAME_ERROR" />
                                )
                            }
                        />
                    }
                    rules={{
                        required: true,
                    }}
                />
            </FormRow>
            <FormRow>
                <Controller
                    name="expiry"
                    control={control}
                    as={
                        <Input
                            id={PAYMENT_CARD_INPUT_IDS.EXPIRY}
                            label={<PureTranslator tk="NEW_CHECKOUT_PAYMENT_FORM_EXPIRY_DATE" />}
                            maskProps={{
                                mask: '99 / 99',
                                maskChar: CREDIT_CARD_MASK_SYMBOL,
                            }}
                            error={
                                errors.expiry && (
                                    <PureTranslator tk="NEW_CHECKOUT_PAYMENT_FORM_EXPIRY_DATE_ERROR" />
                                )
                            }
                        />
                    }
                    rules={{
                        required: true,
                        validate: () =>
                            validateField(
                                'expiry',
                                validateCCExp({
                                    asBool: true,
                                    exp: control.getValues('expiry'),
                                }),
                            ),
                    }}
                />
                <Controller
                    name="cvc"
                    control={control}
                    as={
                        <Input
                            id={PAYMENT_CARD_INPUT_IDS.CVV}
                            label={<PureTranslator tk="NEW_CHECKOUT_PAYMENT_FORM_CVC" />}
                            maskProps={{
                                mask: '9999',
                                maskChar: null,
                            }}
                            error={
                                errors.cvc && (
                                    <PureTranslator tk="NEW_CHECKOUT_PAYMENT_FORM_CVC_ERROR" />
                                )
                            }
                        />
                    }
                    rules={{
                        required: true,
                        minLength: 3,
                        validate: () =>
                            validateField(
                                'cvc',
                                validateCCCvv({ asBool: true, cvv: control.getValues('cvc') }),
                            ),
                    }}
                />
            </FormRow>
            {adyenSubmittedPayment.error.base && adyenSubmittedPayment.error && (
                <ErrorText error>
                    <PureTranslator tk="NEW_CHECKOUT_REDIRECT_FORM_ERROR_TEXT" />
                </ErrorText>
            )}
            <SubmitButton
                small
                disabled={!isValid || (adyenSubmittedPayment && adyenSubmittedPayment.loading)}
                type="submit"
            >
                <PureTranslator tk="CREDIT_CARD_FORM_SUBMIT_BUTTON_LABEL" />
            </SubmitButton>
        </Form>
    );
};

export default AdyenCardForm;
